import { ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { NodeList } from './NodeList'
import type { NodeListConfig, NodeItem, NodeContext } from './NodeList'

// Re-export types from NodeList for convenience
export type { NodeItem, NodeContext, NodeConfig } from './NodeList'

// Main props interface for NodeCard
export interface NodeCardProps<T extends NodeItem = NodeItem> {
  /**
   * Card title
   */
  title?: string
  /**
   * Optional card description
   */
  description?: string
  /**
   * Icon to display next to the title
   */
  icon?: ReactNode
  /**
   * Array of nodes to display
   */
  items: T[]
  /**
   * Context type - determines which List component and configuration to use
   */
  context: NodeContext
  /**
   * Configuration object using the unified NodeListConfig system
   */
  config?: NodeListConfig
  /**
   * Optional action button in the header
   */
  action?: ReactNode
  /**
   * Custom className for the card
   */
  className?: string
}

/**
 * Unified card component that can display any type of node (project, task, message, document)
 * with context-specific configuration and rendering.
 */
export function NodeCard<T extends NodeItem>({
  title,
  description,
  icon,
  items,
  context,
  config = {} as NodeListConfig,
  action,
  className,
}: NodeCardProps<T>) {
  return (
    <Card className={className}>
      {(title || description || action) && (
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              {title && (
                <CardTitle className={icon ? 'flex items-center gap-2' : ''}>
                  {icon}
                  {title}
                </CardTitle>
              )}
              {description && <CardDescription className="mt-2">{description}</CardDescription>}
            </div>
            {action && <div className="flex-shrink-0">{action}</div>}
          </div>
        </CardHeader>
      )}
      <CardContent>
        <NodeList items={items} context={context} config={config} />
      </CardContent>
    </Card>
  )
}
